import { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
import { ClientFile } from '/src/lib/models/ClientFile';

export class ClientFileService {
  /**
   * List ClientFiles
   * @returns ClientFile[]
   */
  public static listClientFiles(
    clientId: string,
    params?: { limit?: number; name?: string }
  ): CancelablePromise<{ data: ClientFile[] }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: `/clients/${clientId}/clientFiles`,
      query: params,
    });
  }

  /**
   * Get ClientFile
   * @returns ClientFile
   */
  public static getClientFile(id: number): CancelablePromise<ClientFile> {
    return __request(OpenAPI, {
      method: 'GET',
      url: `/clientFiles/${id}`,
    });
  }

  /**
   * Get ClientFile URL
   * @returns { url: string }
   */
  public static getClientFileUrl(id: number): CancelablePromise<{ url: string }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: `/clientFiles/${id}/fileUrl`,
    });
  }

  /**
   * Create ClientFile
   * @returns ClientFile
   */
  public static createClientFile(
    clientId: string,
    data: {
      file: File;
      client_file_type_id: number;
      comments: string | null;
      overwrite?: boolean;
    }
  ): CancelablePromise<ClientFile> {
    const typeId =
      typeof data.client_file_type_id === 'object'
        ? (data.client_file_type_id as { id: number }).id
        : Number(data.client_file_type_id);

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('client_file_type_id', typeId.toString());
    formData.append('overwrite', (data.overwrite ?? true).toString());
    formData.append('comments', data.comments === null ? '' : data.comments);

    const requestConfig = {
      method: 'POST' as const,
      url: `/clients/${clientId}/clientFiles`,
      body: formData,
      headers: {
        Accept: 'application/json',
      },
    };

    const promise = __request(OpenAPI, requestConfig) as CancelablePromise<ClientFile>;

    promise.catch(async (error) => {
      console.error('File upload error details:', {
        status: error.status,
        statusText: error.statusText,
        message: error.message,
      });

      if (error.response) {
        try {
          const errorBody = await error.response.text();
          console.error('Error response body:', errorBody);
          try {
            const jsonError = JSON.parse(errorBody);
            console.error('Parsed error response:', jsonError);
          } catch (e) {
            console.error('Error response is not JSON:', errorBody);
          }
        } catch (e) {
          console.error('Could not read error response:', e);
        }
      }
    });

    return promise;
  }

  /**
   * Update ClientFile
   * @returns ClientFile
   */
  public static updateClientFile(
    id: number,
    data: {
      comments: string | null;
    }
  ): CancelablePromise<ClientFile> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: `/clientFiles/${id}`,
      body: data,
    });
  }

  /**
   * Delete ClientFile
   */
  public static deleteClientFile(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: `/clientFiles/${id}`,
    });
  }
}
